import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  NgZone,
  OnDestroy,
  OnInit
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { base64ToFile } from 'ngx-image-cropper';
import {
  validationMessage,
  FEED_PREVIEW_IMAGE_ORIENTATION,
  FEED_PREVIEW_IMAGE_TYPE
} from '../../../../constants/constant';
import { CommonService } from '../../../shared/services/common.service';
import {
  Application,
  BucketFolders
} from '../../../../enums/application.enums';
import { Messages } from 'src/app/enums/messages.enums';
import Compressor from 'compressorjs';
import { UploadService } from '@services/upload.service';
import { base64ToImage } from '@utils/common.utils';
import { API_URLS } from '@core/api-url.enum';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { environment } from '@environments/environment';
import { buildCoverPageImageFileName } from '@modules/whitelabel-cover-page/cover-page-upload.utils';

@Component({
  selector: 'app-banner-image-upload',
  templateUrl: './banner-image.component.html',
  styleUrls: ['./banner-image.component.scss']
})
export class BannerImageUploadComponent implements OnInit, OnDestroy {
  @Input() field: any;
  @Input() form: FormGroup;
  @ViewChild('fileInput') fileInput: ElementRef;
  cropperOptions = {
    cropperMinWidth: 450,
    cropperMinHeight: 250,
    cropperStaticWidth: null,
    cropperStaticHeight: null,
    aspectRatio: null,
    maintainAspectRatio: false
  };
  imageFeedPreview: string | ArrayBuffer = '';
  imageChangedEvent: any = '';
  croppedImage: string = '';
  isOpen = false;
  imageUploadToS3: any[] = [];
  feedPreviewOrientation = FEED_PREVIEW_IMAGE_ORIENTATION.PORTRAIT;
  feedPreviewType = FEED_PREVIEW_IMAGE_TYPE;
  imgWidth = 0;
  application = Application;
  isGifImage: boolean = false;
  percent = Application.ZERO;
  showProgress = false;
  private destroy$ = new Subject<void>();
  constructor(
    public cs: CommonService,
    private readonly uploadService: UploadService,
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone
  ) {}

  ngOnInit(): void {
    const control = this.form?.get(this.field?.name);
    if (!control) {
      return;
    }

    this.syncPreviewFromControlValue(control.value);
    control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.syncPreviewFromControlValue(value);
    });
  }

  private syncPreviewFromControlValue(value: unknown): void {
    if (typeof value === 'string' && value) {
      this.imageFeedPreview = value;
      this.field.bannerPreviewImage = value;
      this.cdr.markForCheck();
      return;
    }

    if (!value) {
      this.imageFeedPreview = '';
      this.field.bannerPreviewImage = '';
      this.cdr.markForCheck();
    }
  }

  async convertHeicToJpeg(e) {
    if (this.cs.isBrowser) {
      const blob: File = e;
      const heicTo = (await import('heic-to')).heicTo;
      return heicTo({
        blob,
        type: 'image/jpeg',
        quality: Number(Application.ONE)
      })
        .then(async (conversionResult: any) => {
          const newName = blob.name.replace(/\.[^/.]+$/, '.jpeg');
          return new File([conversionResult], newName, {
            lastModified: new Date().getTime(),
            type: conversionResult.type
          });
        })
        .catch(() => {
          console.log('Error ');
        });
    }
  }
  uploadImageCompress(file, quality = Application.ZERO_POINT_FOUR as number) {
    if (this.cs.isBrowser) {
      return new Promise(async (resolve, reject) => {
        if (
          file.name.toUpperCase().includes('.HEIC') ||
          file.name.toUpperCase().includes('.HEIF')
        ) {
          file = await this.convertHeicToJpeg(file);
        } else if (
          file.name.toUpperCase().includes('.AVIF')
        ) {
          file = await this.uploadService.convertAvifToJpeg(file);
        }
        new Compressor(file, {
          quality: quality,
          success(result) {
            // Create a new File from the compressed Blob to preserve metadata
            const compressedFile = new File([result], file.name, {
              type: result.type,
              lastModified: file.lastModified
            });
            resolve(compressedFile);
          },
          error(err) {
            reject(err);
          }
        });
      });
    }
  }

  setBannerOrientation(isLandscape: boolean) {
    this.feedPreviewOrientation = isLandscape
      ? FEED_PREVIEW_IMAGE_ORIENTATION.LANDSCAPE
      : FEED_PREVIEW_IMAGE_ORIENTATION.PORTRAIT;
  }

  mobileFileChangeEvent(event) {
    this.imageFeedClicked(event?.[0], true);
  }

  fileChangeEvent(event): void {
    if (this.cs.isBrowser) {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      if (file && file.size < 5000000) {
        const cropperOpts = this.field?.cropperOptions || this.cropperOptions;
        const minWidth = cropperOpts.cropperMinWidth || Application.VIDEO_PREVIEW_WIDTH;
        const minHeight = cropperOpts.cropperMinHeight || Application.VIDEO_PREVIEW_HEIGHT;
        const isGif = file.type === Application.IMAGE_GIF_TYPE;
        
        if (isGif) {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = (e) => {
            const image = new Image();
            image.src = e.target?.result as string;
            image.onload = (le: any) => {
              const newEvent =
                le.path || (le.composedPath && le.composedPath());
              this.imgWidth = newEvent[0].naturalWidth;
              if (
                newEvent[0].naturalWidth >= minWidth &&
                newEvent[0].naturalHeight >= minHeight
              ) {
                this.setBannerOrientation(
                  newEvent[0].naturalWidth > newEvent[0].naturalHeight
                );
                this.imageChangedEvent = event;
                this.imageFeedClicked(null, false, true);
              } else {
                this.cs.showErrorMessage(
                  `${validationMessage.IMAGE_DIMENSION_MUST_BE} ${minWidth}x${minHeight} ${validationMessage.PIXELS}. ${validationMessage.CURRENT_DIMENSIONS}: ${newEvent[0].naturalWidth}x${newEvent[0].naturalHeight} ${validationMessage.PIXELS}.`
                );
                this.removeVideoFeedImage();
              }
            };
            image.onerror = () => {
              this.removeVideoFeedImage();
            };
          };
          reader.onerror = () => {
            this.removeVideoFeedImage();
          };
          return;
        }
        
        this.uploadImageCompress(file).then((res) => {
          const compressedFile: any = res;
          const reader = new FileReader();
          reader.readAsDataURL(compressedFile);
          reader.onload = (e) => {
            const image = new Image();
            image.src = e.target?.result as string;
            image.onload = (le: any) => {
              const newEvent =
                le.path || (le.composedPath && le.composedPath());
              this.imgWidth = newEvent[0].naturalWidth;
              if (
                newEvent[0].naturalWidth >= minWidth &&
                newEvent[0].naturalHeight >= minHeight
              ) {
                const dataTransfer = new DataTransfer();
                dataTransfer.items.add(compressedFile);
                input.files = dataTransfer.files;
                this.setBannerOrientation(
                  newEvent[0].naturalWidth > newEvent[0].naturalHeight
                );
                this.imageChangedEvent = event;
                requestAnimationFrame(() => {
                  this.ngZone.run(() => {
                    this.isOpen = true;
                    this.cdr.detectChanges();
                  });
                });
              } else {
                this.cs.showErrorMessage(
                  `${validationMessage.IMAGE_DIMENSION_MUST_BE} at least ${minWidth}x${minHeight} ${validationMessage.PIXELS}. ${validationMessage.CURRENT_DIMENSIONS}: ${newEvent[0].naturalWidth}x${newEvent[0].naturalHeight} ${validationMessage.PIXELS}.`
                );
              }
            };
            image.onerror = () => {
            };
          };
          reader.onerror = () => {
          };
        });
      } else {
        this.cs.showErrorMessage(validationMessage.imageMax5);
      }
    }
  }

  imageFeedClicked(croppedFile?, isMobileApp = false, isGif = false): void {
    if (croppedFile) {
      this.croppedImage = croppedFile;
    }
    if (this.cs.isBrowser) {
      this.isOpen = false;
      this.cdr.detectChanges();

      let feedBanner: File;
      let uploadFile: File;
      let blob: Blob;

      uploadFile = this.imageChangedEvent?.target?.files?.[Application.ZERO];
      const isGifFile = isGif || (uploadFile && uploadFile.type === Application.IMAGE_GIF_TYPE);

      if (isMobileApp) {
        blob = base64ToImage(croppedFile?.uri, croppedFile?.fileName);
        feedBanner = blob as File;
      } else if (isGifFile) {
        feedBanner = uploadFile;
      } else {
        blob = base64ToFile(this.croppedImage);
        feedBanner = new File([blob], uploadFile.name, {
          type: uploadFile.type
        });
      }

      const isValid = this.cs.isValidFile(feedBanner);
      if (isValid) {
        if (isGifFile) {
          this.handleGifFileUpload(feedBanner);
        } else {
          this.isGifImage = false;
          this.uploadImageCompress(feedBanner)
            .then((res) => {
              const compressedFile: any = res;
              const fReader = new FileReader();
              fReader.onload = (event) => {
                this.field.bannerOrientationPortrait =
                  this.feedPreviewOrientation;
                this.imageFeedPreview = event.target?.result as string;
                this.field.bannerPreviewImage = this.imageFeedPreview;
                this.cdr.detectChanges();
              };
              fReader.readAsDataURL(feedBanner);
              const uploadFolder = this.getImageUploadFolder();
              const videoImage = this.buildUploadFileName(compressedFile.name);
              const obj = {
                file: compressedFile,
                fileName: videoImage,
                folder: uploadFolder
              };
              this.showProgress = true;
              this.uploadService
                .uploadFileData(API_URLS.uploadFile, obj, true)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                  next: (response: any) => {
                    if (response.progressStatus === Application.UPLOADING) {
                      this.percent = response.progress;
                      this.cdr.detectChanges();
                      return;
                    }
                    if (response.progressStatus === Application.COMPLETED) {
                      this.showProgress = false;
                      this.percent = Application.ZERO;
                      const uploadedUrl = environment.s3Config.bucketUrl +
                        uploadFolder + videoImage;
                      this.form.patchValue({
                        [this.field.name]: uploadedUrl
                      });
                      this.cdr.detectChanges();
                    }
                  },
                  error: () => {
                    this.showProgress = false;
                    this.percent = Application.ZERO;
                    this.cs.showErrorMessage(Messages.SOMETHING_WENT_WRONG);
                  }
                });
            })
            .catch(() => {
            });
        }
      } else {
        this.removeVideoFeedImage();
        this.cs.showErrorMessage(Messages.INVALID_FILE_TYPE);
      }
    }
  }

  removeVideoFeedImage(): void {
    this.form.get(this.field.name)?.reset();
    this.imageFeedPreview = '';
    this.imageUploadToS3 = [];
    this.isGifImage = false;
  }

  cancelCrop(): void {
    this.isOpen = false;
  }

  private getImageUploadFolder(): string {
    return this.field?.uploadFolder ?? BucketFolders.createPostImageFolder;
  }

  private buildUploadFileName(originalName: string): string {
    if (this.field?.uploadFolder) {
      return buildCoverPageImageFileName(originalName);
    }
    return `${Application.POST + Date.now()}_${originalName
      .toLowerCase()
      .split(' ')
      .join('_')}`;
  }

  private handleGifFileUpload(file: File): void {
    this.isGifImage = true;
    const fReader = new FileReader();
    fReader.onload = (event) => {
      this.field.bannerOrientationPortrait =
        this.feedPreviewOrientation;
      this.imageFeedPreview = event.target?.result as string;
      this.field.bannerPreviewImage = this.imageFeedPreview;
      this.cdr.detectChanges();
    };
    fReader.readAsDataURL(file);
    const uploadFolder = this.getImageUploadFolder();
    const videoImage = this.buildUploadFileName(file.name);
    const obj = {
      file: file,
      fileName: videoImage,
      folder: uploadFolder
    };
    this.showProgress = true;
    this.uploadService
      .uploadFileData(API_URLS.uploadFile, obj, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.progressStatus === Application.UPLOADING) {
            this.percent = response.progress;
            this.cdr.detectChanges();
            return;
          }
          if (response.progressStatus === Application.COMPLETED) {
            this.showProgress = false;
            this.percent = Application.ZERO;
            const uploadedUrl = environment.s3Config.bucketUrl +
              uploadFolder + videoImage;
            this.form.patchValue({
              [this.field.name]: uploadedUrl
            });
            this.cdr.detectChanges();
          }
        },
        error: () => {
          this.showProgress = false;
          this.percent = Application.ZERO;
          this.cs.showErrorMessage(Messages.SOMETHING_WENT_WRONG);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isCurrentImageGif(): boolean {
    if (this.isGifImage) {
      return true;
    }
    const imageUrl = this.imageFeedPreview || this.form?.get(this.field?.name)?.value;
    if (typeof imageUrl === 'string') {
      return imageUrl.toLowerCase().endsWith('.gif') || imageUrl.toLowerCase().startsWith('data:image/gif');
    }
    return false;
  }
}
